import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTermsAccepted } from '../utils/auth';
import { GuardianAthleteDto } from '../types/guardian';
import { ensureGuardianViewingAthleteId } from '../utils/viewedAthlete';
import TermsAndConditionsModal from './TermsAndConditionsModal';
import { useAuth } from '../contexts/AuthContext';
import polarisLogo from '../images/Polaris Logo.svg';
import '../styles/guardian-select-page.css';

export default function GuardianSelectPage() {
    const navigate = useNavigate();
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [termsCheckComplete, setTermsCheckComplete] = useState(false);
    const { userInfo, isLoading, isAuthenticated } = useAuth();

    useEffect(() => {
        if (isLoading) {
            return;
        }
        if (!isAuthenticated) {
            navigate("/login?error=unauthorized&next=/guardian/select", { replace: true });
            return;
        }
        // Check both localStorage and AuthContext (backend-validated) to avoid race conditions
        const termsAccepted = getTermsAccepted() || userInfo?.termsAndConditionsAccepted;

        if (!termsAccepted) {
            // Show terms modal first
            setShowTermsModal(true);
        } else {
            // Terms are already accepted, proceed with athlete selection
            setShowTermsModal(false);
            setTermsCheckComplete(true);
        }
    }, [isAuthenticated, isLoading, navigate, userInfo?.termsAndConditionsAccepted]);

    // Listen for terms acceptance event
    useEffect(() => {
        const handleTermsAccepted = () => {
            setShowTermsModal(false);
            setTermsCheckComplete(true);
        };

        window.addEventListener('termsAccepted', handleTermsAccepted);
        return () => {
            window.removeEventListener('termsAccepted', handleTermsAccepted);
        };
    }, []);

    // Once terms check is complete and the session is hydrated, proceed with athlete selection
    useEffect(() => {
        if (termsCheckComplete && !isLoading && isAuthenticated) {
            proceedWithAthleteSelection();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, isLoading, termsCheckComplete]);

    const proceedWithAthleteSelection = () => {
        // Get guardian athletes from localStorage
        const athletesData = localStorage.getItem('guardianAthletes');

        if (!athletesData) {
            navigate('/guardian/no-athletes');
            return;
        }

        try {
            const parsedAthletes: GuardianAthleteDto[] = JSON.parse(athletesData);

            if (!parsedAthletes || parsedAthletes.length === 0) {
                navigate('/guardian/no-athletes');
                return;
            }

            const athleteId = ensureGuardianViewingAthleteId();
            if (!athleteId) {
                navigate('/guardian/no-athletes');
                return;
            }
            navigate('/athlete/home', { replace: true });
        } catch (error) {
            navigate('/guardian/no-athletes');
        }
    };

    const handleModalClose = () => {
        // If user closes the modal without accepting, redirect to login
        if (!getTermsAccepted()) {
            navigate('/login', { replace: true });
        }
        // If they accepted, the termsAccepted event will handle the flow
    };

    return (
        <div className="guardian-select-page">
            {/* Polaris Brand Banner */}
            <div className="guardian-brand-banner">
                <img src={polarisLogo} alt="Polaris Athlete" className="guardian-brand-logo" />
            </div>

            {/* Main Content */}
            {/* Always render the branded loading card so the page never looks blank. On first login
                the T&C modal is a full-screen overlay rendered on top of it, so the background shows a
                proper "setting up your account" state instead of a bare white page. */}
            <div className="guardian-content">
                <div className="guardian-loading-card">
                    <div className="guardian-spinner"></div>
                    <h2>Loading...</h2>
                    <p>Please wait while we set up your account.</p>
                </div>
            </div>

            {showTermsModal && (
                <TermsAndConditionsModal
                    open={showTermsModal}
                    onClose={handleModalClose}
                />
            )}
        </div>
    );
}

